/**
 * propagate.js – SGP4 helpers shared by 2D (Leaflet) and 3D (Three.js)
 */

import * as satellite from "satellite.js";

/**
 * Returns { lat, lon, alt, velocity } for a TLE at `date` (default: now).
 */
export function propagateNow(tle1, tle2, date = new Date()) {
  try {
    const satrec = satellite.twoline2satrec(tle1, tle2);
    const pv     = satellite.propagate(satrec, date);
    if (!pv.position) return null;

    const gmst = satellite.gstime(date);
    const geo  = satellite.eciToGeodetic(pv.position, gmst);

    const lat      = satellite.degreesLat(geo.latitude);
    const lon      = satellite.degreesLong(geo.longitude);
    const alt      = geo.height;
    const v        = pv.velocity;
    const velocity = Math.sqrt(v.x ** 2 + v.y ** 2 + v.z ** 2);

    return { lat, lon, alt, velocity };
  } catch {
    return null;
  }
}

/**
 * Returns an array of { lat, lon, alt } points for the next `minutesAhead`
 * minutes, sampled every `stepSec` seconds.
 * Anti-meridian crossings are marked with `null` separators.
 */
export function groundTrackPoints(tle1, tle2, { minutesAhead = 90, stepSec = 60 } = {}) {
  try {
    const satrec = satellite.twoline2satrec(tle1, tle2);
    const steps  = Math.floor((minutesAhead * 60) / stepSec);
    const now    = Date.now();
    const pts    = [];
    let prevLon  = null;

    for (let i = 0; i <= steps; i++) {
      const date = new Date(now + i * stepSec * 1000);
      const pv   = satellite.propagate(satrec, date);
      if (!pv.position) continue;

      const gmst = satellite.gstime(date);
      const geo  = satellite.eciToGeodetic(pv.position, gmst);
      const lat  = satellite.degreesLat(geo.latitude);
      const lon  = satellite.degreesLong(geo.longitude);
      const alt  = geo.height;

      if (prevLon !== null && Math.abs(lon - prevLon) > 180) pts.push(null);

      pts.push({ lat, lon, alt });
      prevLon = lon;
    }
    return pts;
  } catch {
    return [];
  }
}
